import { FC, useState } from 'react';
import { useParams } from 'react-router';
import { Box, Divider, Flex, VStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { isNil } from 'ramda';

import { BankAccountStatus } from '__generated__/GQL';
import bankAccountLocales from 'common/bankAccount/locales';
import { getBankAccountDisplayName } from 'common/bankAccount/utils';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import SunshineModal from 'components/_core/SunshineModal';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import NavigationButton from 'components/NavigationButton';
import { formatDate, PPP } from 'helpers/date';

import DirectDebitMandateBadge from '../DirectDebitMandateBadge';
import DirectDebitMandateOldIbanWarning from '../DirectDebitMandateOldIbanWarning';
import { useDirectDebitMandateQuery } from '../hooks/useDirectDebitMandateQuery';
import { useRevokeDirectDebitMandateMutation } from '../hooks/useRevokeDirectDebitMandateMutation';
import { useUnrevokeDirectDebitMandateMutation } from '../hooks/useUnrevokeDirectDebitMandateMutation';
import locales from './locales';
import * as S from './styles';

const DISPUTE_MANDATE_FAQ_ARTICLE_URL =
  'https://help.shine.fr/fr/articles/3629915-comment-contester-un-prelevement-sepa-b2b';

interface DirectDebitMandateSidebarParams {
  directDebitMandateId: string;
}

const DirectDebitMandateSidebar: FC = () => {
  const { directDebitMandateId } = useParams<DirectDebitMandateSidebarParams>();

  const { bankAccount, directDebitMandate, error, loading, refetch } =
    useDirectDebitMandateQuery(directDebitMandateId);

  const [
    revokeDirectDebitMandate,
    { loading: revokeDirectDebitMandateLoading },
  ] = useRevokeDirectDebitMandateMutation(directDebitMandateId);
  const [
    unrevokeDirectDebitMandate,
    { loading: unrevokeDirectDebitMandateLoading },
  ] = useUnrevokeDirectDebitMandateMutation(directDebitMandateId);

  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [isUnrevokeModalOpen, setIsUnrevokeModalOpen] = useState(false);

  if (loading) {
    return (
      <S.Root>
        <Loader />
      </S.Root>
    );
  }
  if (error || !directDebitMandate) {
    return (
      <S.Root>
        <Flex justifyContent="flex-end" marginBottom="space-32">
          <NavigationButton to="/payments/mandates" variant="close" />
        </Flex>

        <ErrorCard retry={refetch} />
      </S.Root>
    );
  }

  const {
    creditorIdentifier,
    creditorName,
    isOldDebtorIban,
    isRevoked,
    label,
    publicUrl,
    revokedAt,
    scheme,
    uniqueMandateReference,
  } = directDebitMandate;

  const bankAccountName = bankAccount
    ? getBankAccountDisplayName(bankAccount)
    : bankAccountLocales.mainAccount;

  const isBankAccountClosed = bankAccount?.status === BankAccountStatus.Closed;

  const details = [
    {
      label: locales.recipient,
      value: creditorName,
    },
    {
      label: locales.uniqueMandateReference,
      value: uniqueMandateReference,
    },
    {
      label: locales.creditorIdentifier,
      value: creditorIdentifier,
    },
  ];
  if (isRevoked && revokedAt) {
    details.push({
      label: locales.revokedAt,
      value: formatDate(new Date(revokedAt), PPP),
    });
  }

  const isB2BMandate = scheme === 'SDD_B2B';
  const hasSignedMandate = isB2BMandate && !isNil(publicUrl);

  const oldIbanWarning = isB2BMandate
    ? locales.b2bOldIbanWarning
    : locales.coreOldIbanWarning;

  const onRevokeMandate = async () => {
    try {
      await revokeDirectDebitMandate();
      setIsRevokeModalOpen(false);
    } catch (_err) {
      // The error will be handled by the hook
    }
  };
  const onUnrevokeMandate = async () => {
    try {
      await unrevokeDirectDebitMandate();
      setIsUnrevokeModalOpen(false);
    } catch (_err) {
      // The error will be handled by the hook
    }
  };

  return (
    <S.Root>
      <Flex justify="flex-end" marginBottom="space-32">
        <NavigationButton to="/payments/mandates" variant="close" />
      </Flex>

      <motion.div
        animate={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: -10 }}
        key={directDebitMandateId}
      >
        <VStack align="flex-start" marginBottom="space-32" spacing="space-8">
          <Heading size="lg">{label || creditorName}</Heading>
          <DirectDebitMandateBadge isRevoked={isRevoked} />
        </VStack>

        <Box paddingTop="space-16">
          <Text fontWeight="weight-500" marginBottom="space-20" size="lg">
            {locales.mandateDetails}
          </Text>

          {isOldDebtorIban ? (
            <DirectDebitMandateOldIbanWarning isB2BMandate={isB2BMandate} />
          ) : null}

          <VStack
            align="stretch"
            divider={<Divider />}
            marginBottom="space-32"
            spacing="space-16"
          >
            <Flex
              alignItems="start"
              direction="column"
              key={locales.bankAccount}
            >
              <Text variant="light">{locales.bankAccount}</Text>
              {isBankAccountClosed ? (
                <Text variant="disabled">
                  {locales.formatString(locales.bankAccountClosed, {
                    bankAccountName,
                  })}
                </Text>
              ) : (
                <Text variant="primary">{bankAccountName}</Text>
              )}
            </Flex>

            {details.map((detail) => (
              <Flex alignItems="start" direction="column" key={detail.label}>
                <Text variant="light">{detail.label}</Text>
                <Text variant="primary">{detail.value}</Text>
              </Flex>
            ))}
          </VStack>
        </Box>

        {hasSignedMandate ? (
          <SunshineCard
            alignItems="center"
            as="a"
            href={publicUrl}
            marginBottom="space-32"
            target="_blank"
            title={locales.showMandate}
          >
            <SunshineCard.Slot name="aside">
              <SquircleIcon name="paper-clip" />
            </SunshineCard.Slot>

            <SunshineCard.Slot name="action">
              <SunshineIcon name="chevron-right" size="icon-20" />
            </SunshineCard.Slot>
          </SunshineCard>
        ) : null}

        {!isBankAccountClosed ? (
          <Flex justifyContent="stretch" marginBottom="space-32">
            {isRevoked ? (
              <Button
                onClick={() => setIsUnrevokeModalOpen(true)}
                variant="secondary"
                width="full"
              >
                {locales.unrevokeMandate}
              </Button>
            ) : (
              <Button
                onClick={() => setIsRevokeModalOpen(true)}
                variant="danger"
                width="full"
              >
                {locales.revokeMandate}
              </Button>
            )}
          </Flex>
        ) : null}

        {isB2BMandate ? (
          <Callout>
            <Callout.Title>{locales.warningTitle}</Callout.Title>

            <Callout.Content>
              {locales.warnings.map((text) => text)}
            </Callout.Content>

            <Callout.Button
              as="a"
              href={DISPUTE_MANDATE_FAQ_ARTICLE_URL}
              target="_blank"
            >
              {locales.learnMore}
            </Callout.Button>
          </Callout>
        ) : null}
      </motion.div>

      <SunshineModal
        isOpen={isRevokeModalOpen}
        onClose={() => setIsRevokeModalOpen(false)}
      >
        <SunshineModal.Header />

        <SunshineModal.Body>
          <Heading marginBottom="space-24">{locales.modalRevoke.title}</Heading>
          <Text marginBottom="space-32">{locales.modalRevoke.description}</Text>

          {isOldDebtorIban ? (
            <Callout marginBottom="space-32" textAlign="left" variant="warning">
              <Callout.Content>{oldIbanWarning}</Callout.Content>
            </Callout>
          ) : null}
        </SunshineModal.Body>

        <SunshineModal.Footer justifyContent="space-between">
          <Button
            onClick={() => setIsRevokeModalOpen(false)}
            variant="inline-secondary"
          >
            {locales.actionCancel}
          </Button>

          <Button
            isLoading={revokeDirectDebitMandateLoading}
            onClick={onRevokeMandate}
            variant="danger"
          >
            {locales.actionAccept}
          </Button>
        </SunshineModal.Footer>
      </SunshineModal>

      <SunshineModal
        isOpen={isUnrevokeModalOpen}
        onClose={() => setIsUnrevokeModalOpen(false)}
      >
        <SunshineModal.Header />
        <SunshineModal.Body>
          <Heading marginBottom="space-24">
            {locales.modalUnrevoke.title}
          </Heading>
          <Text marginBottom="space-32">
            {locales.modalUnrevoke.description}
          </Text>
        </SunshineModal.Body>

        <SunshineModal.Footer justifyContent="space-between">
          <Button
            onClick={() => setIsUnrevokeModalOpen(false)}
            variant="inline-secondary"
          >
            {locales.actionCancel}
          </Button>
          <Button
            isLoading={unrevokeDirectDebitMandateLoading}
            onClick={onUnrevokeMandate}
          >
            {locales.actionAccept}
          </Button>
        </SunshineModal.Footer>
      </SunshineModal>
    </S.Root>
  );
};

export default DirectDebitMandateSidebar;
